import React, { useState, useEffect } from 'react';
import { Save, Trash2, Eye, Upload as UploadIcon, Settings } from 'lucide-react';
import ImageUpload from '../components/ImageUpload';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPanel = () => {
  const [activeTab, setActiveTab] = useState('images');
  const [profileImages, setProfileImages] = useState({
    primary: null,
    secondary: null
  });
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadCurrentImages();
  }, []);

  const loadCurrentImages = async () => {
    try {
      const response = await axios.get(`${API}/profile`);
      if (response.data) {
        setProfileImages({
          primary: response.data.image,
          secondary: response.data.secondaryImage
        });
      }
    } catch (error) {
      console.error('Error loading current images:', error);
    }
  };

  const handleImageUploaded = async (imageData) => {
    try {
      setIsLoading(true);
      
      // Set the uploaded image as active
      const response = await axios.post(`${API}/set-profile-image`, {
        filename: imageData.filename,
        image_type: imageData.image_type
      });

      if (response.data.success) {
        setMessage(`${imageData.image_type === 'primary' ? 'Hauptbild' : 'Sekundärbild'} erfolgreich aktualisiert!`);
        
        // Update local state
        setProfileImages(prev => ({
          ...prev,
          [imageData.image_type]: imageData.base64_url
        }));
        
        // Clear message after 3 seconds
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (error) {
      setMessage('Fehler beim Aktualisieren des Bildes');
      console.error('Error setting profile image:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const previewChanges = () => {
    window.open('/', '_blank');
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Settings size={24} className="text-violet-500 mr-3" />
            <h1 className="text-2xl font-bold">Stefan Hay - Admin Panel</h1>
          </div>
          <button
            onClick={previewChanges}
            className="flex items-center px-4 py-2 bg-violet-600 hover:bg-violet-700 rounded-lg transition-colors"
          >
            <Eye size={20} className="mr-2" />
            Website anzeigen
          </button>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-gray-800 border-b border-gray-700 px-6 py-2">
        <div className="flex space-x-6">
          <button
            onClick={() => setActiveTab('images')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              activeTab === 'images'
                ? 'bg-violet-600 text-white'
                : 'text-gray-300 hover:text-white hover:bg-gray-700'
            }`}
          >
            Bilder verwalten
          </button>
        </div>
      </nav>

      {/* Main Content */}
      <main className="p-6">
        {/* Success Message */}
        {message && (
          <div className="mb-6 p-4 bg-green-900/20 border border-green-500/30 rounded-lg">
            <p className="text-green-300">{message}</p>
          </div>
        )}

        {activeTab === 'images' && (
          <div className="max-w-4xl mx-auto">
            <div className="mb-8">
              <h2 className="text-3xl font-bold mb-2">Profilbilder verwalten</h2>
              <p className="text-gray-400">
                Laden Sie Ihre Profilbilder hoch und verwalten Sie diese für die Website.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Primary Image */}
              <div className="bg-gray-800 rounded-lg p-6">
                <h3 className="text-xl font-semibold mb-4">Hauptprofilbild</h3>
                <p className="text-gray-400 mb-4">
                  Dieses Bild wird auf der Homepage im Hero-Bereich angezeigt.
                </p>
                <ImageUpload
                  imageType="primary"
                  currentImage={profileImages.primary}
                  onImageUploaded={handleImageUploaded}
                />
              </div>

              {/* Secondary Image */}
              <div className="bg-gray-800 rounded-lg p-6">
                <h3 className="text-xl font-semibold mb-4">Sekundäres Profilbild</h3>
                <p className="text-gray-400 mb-4">
                  Dieses Bild wird auf der Über-mich-Seite und anderen Bereichen verwendet.
                </p>
                <ImageUpload
                  imageType="secondary"
                  currentImage={profileImages.secondary}
                  onImageUploaded={handleImageUploaded}
                />
              </div>
            </div>

            {/* Preview Section */}
            <div className="mt-12 bg-gray-800 rounded-lg p-6">
              <h3 className="text-xl font-semibold mb-4">Vorschau</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {profileImages.primary && (
                  <div>
                    <h4 className="text-lg font-medium mb-2">Hauptbild</h4>
                    <img
                      src={profileImages.primary}
                      alt="Primary profile"
                      className="w-48 h-48 object-cover rounded-full border-4 border-violet-500"
                    />
                  </div>
                )}
                {profileImages.secondary && (
                  <div>
                    <h4 className="text-lg font-medium mb-2">Sekundärbild</h4>
                    <img
                      src={profileImages.secondary}
                      alt="Secondary profile"
                      className="w-48 h-64 object-cover rounded-lg border-4 border-teal-500"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Instructions */}
            <div className="mt-8 bg-violet-900/20 border border-violet-500/30 rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-3">Anleitung</h3>
              <div className="space-y-2 text-gray-300">
                <p>• Ziehen Sie Bilder per Drag & Drop in die Upload-Bereiche</p>
                <p>• Unterstützte Formate: JPG, PNG (max. 10MB)</p>
                <p>• Bilder werden automatisch optimiert und zugeschnitten</p>
                <p>• Hauptbild wird quadratisch (400x400px) zugeschnitten</p>
                <p>• Sekundärbilder behalten das Seitenverhältnis bei</p>
                <p>• Änderungen werden sofort auf der Website sichtbar</p>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminPanel;